import { ReactNode } from 'react';
import { useLogger, useServices } from '@packages/shared/core/common/hooks';
import { useQuizStep } from '@packages/shared/core/quiz/hooks';
import { NameStepData, PhoneNumberStepData, QuizScreenProps } from '@packages/shared/core/quiz/entities';
import { normalizeError } from '@packages/shared/core/common/utils/errors';
import { useLocation } from '@packages/shared/core/location/hooks';

type PhoneNumberQuizStepProps = {
  id: string;
  renderQuizScreen: (stepData: QuizScreenProps) => ReactNode;
};

const PhoneNumberQuizStep = ({ id, renderQuizScreen }: PhoneNumberQuizStepProps) => {
  const { stepData, totalSteps, stepOrder, updateUserPreferences, next } = useQuizStep(id);

  const { country: userCountry } = useLocation();

  const logger = useLogger();
  const { userService, analyticsService } = useServices();

  if (!stepData) {
    logger.error(new TypeError(`Data for ${id} step not found`));
    return null;
  }

  const { dataKey } = stepData as NameStepData;

  const onSubmit = async (phoneNumber: string) => {
    analyticsService.smsInputScreenClicked({ eventValue: 'opted_in' });

    try {
      await userService.updateUserInformation({ phone_number: phoneNumber });

      updateUserPreferences(dataKey, phoneNumber);
    } catch (err) {
      const error = normalizeError(err);

      logger.error(error);
    } finally {
      await next();
    }
  };

  const skip = async () => {
    analyticsService.smsInputScreenClicked({ eventValue: 'closed' });
    next();
  };

  return (
    <>
      {renderQuizScreen({
        totalSteps,
        stepOrder,
        onSubmit,
        userCountry,
        skip,
        ...(stepData as PhoneNumberStepData),
      })}
    </>
  );
};

export default PhoneNumberQuizStep;
